import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { documentsAPI, SITE_COLORS } from '../../lib/api';
import { formatDate, CATEGORY_LABELS } from '../../lib/utils';
import { ArrowLeft, FileText, Download, FolderOpen } from 'lucide-react';
import { SEO } from '../seo/SEO';

export const DocumentsPage = ({ siteKey }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category');
  const colors = SITE_COLORS[siteKey];

  useEffect(() => {
    const loadDocuments = async () => {
      try {
        const response = await documentsAPI.getAll(siteKey, category);
        setDocuments(response.data);
      } catch (error) {
        console.error('Error loading documents:', error);
      } finally {
        setLoading(false);
      }
    };
    loadDocuments();
  }, [siteKey, category]);

  const categories = [
    { key: null, label: 'Všechny dokumenty' },
    { key: 'uredni_deska', label: 'Úrední deska' },
    { key: 'pro_rodice', label: 'Pro rodice' },
    { key: 'formulare', label: 'Formuláre' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="py-12">
      <SEO
        siteKey={siteKey}
        title={category ? CATEGORY_LABELS[category] : 'Dokumenty'}
        description={`Důležité dokumenty a formuláře ke stažení - ${siteKey === 'ms' ? 'MŠ Cestička' : 'dětská skupina Chaloupka na vršku'}. Školní řád, přihlášky, směrnice a další.`}
        keywords={['dokumenty', 'formuláře', 'ke stažení', 'školní řád']}
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <Link to={`/${siteKey}`} className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#45AAF2] mb-4" data-testid="back-link">
            <ArrowLeft size={18} />
            Zpet na uvodní stránku
          </Link>
          <h1 className="text-4xl font-bold text-[#2D3748]" data-testid="page-title">
            {category ? CATEGORY_LABELS[category] : 'Dokumenty'}
          </h1>
          <p className="text-[#64748B] mt-2">Dulezité dokumenty a formuláre ke stažení</p>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <Link
              key={cat.key || 'all'}
              to={cat.key ? `/${siteKey}/dokumenty?category=${cat.key}` : `/${siteKey}/dokumenty`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                category === cat.key
                  ? 'text-white'
                  : 'bg-white text-[#64748B] hover:bg-[#E8F4FD] border border-slate-200'
              }`}
              style={category === cat.key ? { background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` } : {}}
              data-testid={`category-${cat.key || 'all'}`}
            >
              {cat.label}
            </Link>
          ))}
        </div>

        {documents.length > 0 ? (
          <div className="space-y-3">
            {documents.map((doc, index) => (
              <div 
                key={doc.id} 
                className="document-card animate-fade-in-up"
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`document-${index}`}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${colors.primary}15` }}>
                  <FileText size={24} style={{ color: colors.primary }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-[#2D3748] truncate">{doc.title}</h3>
                  {doc.description && (
                    <p className="text-sm text-[#64748B] truncate">{doc.description}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1 text-xs text-[#64748B]">
                    <span>{CATEGORY_LABELS[doc.category] || doc.category}</span>
                    <span>-</span>
                    <span>{formatDate(doc.created_at)}</span>
                  </div>
                </div>
                <a 
                  href={doc.file_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-medium hover:opacity-90 transition-opacity flex-shrink-0"
                  style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
                  data-testid={`download-${index}`}
                >
                  <Download size={16} />
                  <span className="hidden sm:inline">Stáhnout</span>
                </a>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-white rounded-2xl border-3 border-[#F0F4F8]">
            <FolderOpen size={48} className="mx-auto text-[#CBD5E1] mb-4" />
            <div className="text-[#64748B]">
              {category 
                ? `V kategorii "${CATEGORY_LABELS[category]}" nejsou žádné dokumenty` 
                : 'Zatím nejsou žádné dokumenty'
              }
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsPage;
